import React from 'react';
import Box from 'Components/Atoms/Box';
import useCollection from 'CustomHooks/useCollection';
import firebase from 'utils/firebase';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import WarningIcon from '@material-ui/icons/Warning';
import InfoIcon from '@material-ui/icons/Info';

interface DashboardAlertsProps {
  restaurantId: string | any;
}

type AlertDoc = {
  description: string;
  isGeneral?: boolean;
  restaurantId?: string;
  createdAt: firebase.firestore.Timestamp | null;
  severity?: 'error' | 'warning' | 'info';
};

const ALERT_LIMIT = 100;

const DashboardAlerts: React.FC<DashboardAlertsProps> = ({ restaurantId }) => {
  // Two filtered subscriptions instead of one unfiltered query: the Firestore
  // rule denies the whole collection read unless every matched doc is readable
  // (isGeneral || waiter of restaurantId). Each single-field `where` is
  // auto-indexed; we deliberately drop `orderBy` here to avoid a composite
  // index and sort/limit client-side after merging.
  const [generalAlertsCol] = useCollection<AlertDoc>('dashboardAlertMessages', {
    filter: [['isGeneral', '==', true]],
  });

  const restaurantAlertsPath = restaurantId ? 'dashboardAlertMessages' : '';

  const [restaurantAlertsCol] = useCollection<AlertDoc>(restaurantAlertsPath, {
    filter: [['restaurantId', '==', restaurantId ?? '']],
  });

  const filteredAlerts = React.useMemo(() => {
    const byId = new Map<string, AlertDoc & { id: string }>();

    for (const alert of [
      ...(generalAlertsCol.data ?? []),
      ...(restaurantAlertsCol.data ?? []),
    ]) {
      byId.set(alert.id, alert);
    }

    return Array.from(byId.values())
      .sort((a, b) => {
        const aMs = a.createdAt?.toMillis() ?? 0;
        const bMs = b.createdAt?.toMillis() ?? 0;
        return bMs - aMs;
      })
      .slice(0, ALERT_LIMIT);
  }, [generalAlertsCol.data, restaurantAlertsCol.data]);

  const [currentIndex, setCurrentIndex] = React.useState(0);

  React.useEffect(() => {
    if (currentIndex >= filteredAlerts.length) {
      setCurrentIndex(
        filteredAlerts.length > 0 ? filteredAlerts.length - 1 : 0
      );
    }
  }, [filteredAlerts.length, currentIndex]);
  const [animate, setAnimate] = React.useState(false);
  const isSingleAlert = filteredAlerts.length <= 1;

  React.useEffect(() => {
    if (filteredAlerts.length <= 1) {
      setAnimate(true);
      return;
    }

    setAnimate(false);
    const timeout = setTimeout(() => setAnimate(true), 10);

    const interval = setInterval(() => {
      setAnimate(false);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % filteredAlerts.length);
        setAnimate(true);
      }, 400);
    }, 4000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [filteredAlerts.length]);

  if (filteredAlerts.length === 0) return null;

  const getSeverityProps = (
    sev: 'error' | 'warning' | 'info' | undefined
  ): {
    color: string;
    bg: string;
    Icon: React.ElementType;
  } => {
    switch (sev) {
      case 'warning':
        return {
          color: '#ff9800',
          bg: 'rgba(255, 152, 0, 0.1)',
          Icon: WarningIcon,
        };
      case 'info':
        return {
          color: '#2196f3',
          bg: 'rgba(33, 150, 243, 0.1)',
          Icon: InfoIcon,
        };
      case 'error':
      default:
        return {
          color: '#f44336',
          bg: 'rgba(244, 67, 54, 0.1)',
          Icon: ErrorOutlineIcon,
        };
    }
  };

  const currentAlert = filteredAlerts[currentIndex];
  const { color, bg, Icon } = getSeverityProps(currentAlert?.severity as any);

  return (
    <div style={{ marginTop: 16, width: '100%' }}>
      <Box
        style={{
          width: '100%',
          background: bg,
          border: `1px solid ${color}`,
          color: color,
          borderRadius: 8,
          padding: '12px 16px',
          fontWeight: 500,
          fontSize: '1rem',
          minHeight: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Icon style={{ marginRight: 18, color: color, flexShrink: 0 }} />
        <div
          style={{
            display: 'inline-block',
            whiteSpace: 'normal',
            position: 'relative',
            left: 0,
            right: 0,
            marginLeft: 10,
            transition:
              'transform 0.4s cubic-bezier(.4,0,.2,1), opacity 0.4s cubic-bezier(.4,0,.2,1)',
            transform: isSingleAlert
              ? 'none'
              : animate
              ? 'translateX(0%)'
              : 'translateX(100%)',
            opacity: isSingleAlert ? 1 : animate ? 1 : 0,
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: filteredAlerts[currentIndex]?.description || '',
            }}
          />
        </div>
      </Box>
    </div>
  );
};

export default DashboardAlerts;
